import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { FloorLayoutInfo, ShiftInfo } from '@/types/api';
import { X } from 'lucide-react';

interface LayoutModalProps {
  restaurantId: number;
  floorId: number;
  layout: FloorLayoutInfo | null;
  shifts: ShiftInfo[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function LayoutModal({ restaurantId, floorId, layout, shifts, onClose, onSuccess }: LayoutModalProps) {
  const [formData, setFormData] = useState({
    name: layout?.name || '',
    designWidth: layout?.designWidth ?? 800,
    designHeight: layout?.designHeight ?? 600,
    shiftId: layout?.shiftId?.toString() || '',
    isDefault: layout?.isDefault ?? false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: (data: Partial<FloorLayoutInfo>) =>
      apiClient.createLayout(restaurantId, floorId, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create layout';
      setErrors({ submit: message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<FloorLayoutInfo>) =>
      apiClient.updateLayout(restaurantId, layout!.id, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update layout';
      setErrors({ submit: message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Layout name is required';
    }

    if (!formData.designWidth || formData.designWidth <= 0) {
      newErrors.designWidth = 'Design width must be greater than 0';
    }

    if (!formData.designHeight || formData.designHeight <= 0) {
      newErrors.designHeight = 'Design height must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const submitData: Partial<FloorLayoutInfo> = {
      name: formData.name,
      designWidth: formData.designWidth,
      designHeight: formData.designHeight,
      shiftId: formData.shiftId ? Number(formData.shiftId) : undefined,
      isDefault: formData.isDefault,
    };

    if (layout) {
      await updateMutation.mutateAsync(submitData);
    } else {
      await createMutation.mutateAsync(submitData);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {layout ? 'Edit Layout' : 'Add New Layout'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="layout-name" className="block text-sm font-medium text-gray-700 mb-1">
              Layout Name *
            </label>
            <input
              id="layout-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
              placeholder="e.g., Default Layout, Lunch Setup"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="layout-width" className="block text-sm font-medium text-gray-700 mb-1">
                Design Width *
              </label>
              <input
                id="layout-width"
                type="number"
                value={formData.designWidth}
                onChange={(e) => setFormData({ ...formData, designWidth: Number(e.target.value) })}
                className={`input w-full ${errors.designWidth ? 'border-red-500' : ''}`}
                placeholder="e.g., 800"
                min={1}
              />
              {errors.designWidth && <p className="text-red-500 text-xs mt-1">{errors.designWidth}</p>}
            </div>
            <div>
              <label htmlFor="layout-height" className="block text-sm font-medium text-gray-700 mb-1">
                Design Height *
              </label>
              <input
                id="layout-height"
                type="number"
                value={formData.designHeight}
                onChange={(e) => setFormData({ ...formData, designHeight: Number(e.target.value) })}
                className={`input w-full ${errors.designHeight ? 'border-red-500' : ''}`}
                placeholder="e.g., 600"
                min={1}
              />
              {errors.designHeight && <p className="text-red-500 text-xs mt-1">{errors.designHeight}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="layout-shift" className="block text-sm font-medium text-gray-700 mb-1">
              Shift
            </label>
            <select
              id="layout-shift"
              value={formData.shiftId}
              onChange={(e) => setFormData({ ...formData, shiftId: e.target.value })}
              className="input w-full"
            >
              <option value="">Default (All Shifts)</option>
              {shifts.map((shift) => (
                <option key={shift.id} value={shift.id}>
                  {shift.name} ({shift.startTime} - {shift.endTime})
                </option>
              ))}
            </select>
            <p className="text-gray-400 text-xs mt-1">
              Optionally assign this layout to a specific shift.
            </p>
          </div>

          <div className="flex items-center">
            <input
              id="layout-default"
              type="checkbox"
              checked={formData.isDefault}
              onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="layout-default" className="ml-2 text-sm font-medium text-gray-700">
              Set as default layout
            </label>
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : layout
                ? 'Update Layout'
                : 'Create Layout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
